import AsciiAssembly from "@/components/AsciiAssembly/AsciiAssembly"
import GridBackdrop from "@/components/GridBackdrop/GridBackdrop"
import LokaMark from "@/components/LokaMark/LokaMark"
import { REPORT_MARK, REPORT_MARK_COLUMNS } from "@/content/ascii"
import { GENERATING } from "@/content/overlays"
import { useAssembly } from "@/hooks/useAssembly"
import { assembleMark, reveal, overshoot, writeLines } from "@/utils/glyphMotion"
import type { ReportTally } from "@/content/overlays"
import styles from "@/styles/components/GeneratingScreen.module.css"

/**
 * The beat between closing the last job and the debrief appearing.
 *
 * NOTHING IS BEING COMPUTED. The report is derived from the job documents on
 * every render and is never stored, so there is no work to wait for — the write
 * underneath this has usually already landed. What the pause buys is that the
 * room has just spent an hour producing this, and a leave-behind that appears
 * instantly reads as a page change rather than as an outcome. The tally names
 * what is on the sheet, so the time is spent reporting the session back rather
 * than stalling.
 *
 * The motion is `Report generation & Break screens explorations.dc`, screen 7b,
 * which is the approved one: the mark assembles out of scattered glyphs on the
 * left while the tally is decoded line by line on the right, with the blue bar
 * across the top as the only loader. Both halves are driven by the one clock in
 * `useAssembly` — see `utils/glyphMotion` for why that matters.
 *
 * Because it is presentation, it is skipped entirely under
 * `prefers-reduced-motion`: `useAssembly` hands back a finished arc, so a reader
 * who has asked for less motion sees the assembled mark and the whole tally at
 * once rather than watching it be written.
 *
 * Deliberately NOT used on `/reports/:sessionId`. Opening an old report from the
 * index is navigation, and a manufactured pause there would be a lie about a
 * document that is already sitting in front of you.
 */

/**
 * How long the screen is up for, and therefore how long the arc has to run in.
 *
 * Exported because the caller is what actually holds it there — the animation
 * cannot be cut short by a shorter timeout without the tally's last line being
 * left mid-scramble, which reads as the report having failed.
 */
export const GENERATING_MS = 4700

/** Where the mark and the tally sit in the arc. From the reference. */
const MARK_BOX = { top: 0.02, height: 0.6 }
const TALLY_BOX = { top: 0.4, height: 0.44 }
/** The title lands between them. */
const TITLE_AT = 0.3
const TITLE_WINDOW = 0.16

interface GeneratingScreenProps {
  /** What the room actually produced. See `ReportTally`. */
  tally: ReportTally
}

export default function GeneratingScreen({ tally }: GeneratingScreenProps) {
  const progress = useAssembly(GENERATING_MS)

  const rows = assembleMark(progress, REPORT_MARK, MARK_BOX)
  const lines = writeLines(progress, GENERATING.lines(tally), TALLY_BOX)
  const titleIn = reveal(progress, TITLE_AT, TITLE_WINDOW)

  return (
    <div className={styles.curtain} role="status" aria-live="polite">
      <GridBackdrop />
      <LokaMark className={styles.wordmark} />

      {/* The one loader on the screen. It runs slightly ahead of the arc so it
          reads as full while the last line is still resolving, rather than
          finishing after the thing it is measuring. */}
      <div className={styles.track} aria-hidden="true">
        <div className={styles.bar} style={{ width: `${Math.min(1, progress * 1.03) * 100}%` }} />
      </div>

      <div className={styles.inner}>
        <AsciiAssembly
          rows={rows}
          columns={REPORT_MARK_COLUMNS}
          cell="6px"
          fontSize="9px"
          lineHeight="10px"
          className={styles.mark}
        />

        <div className={styles.readout}>
          <h1
            className={styles.title}
            style={{
              opacity: titleIn,
              transform: `translateY(${(14 * (1 - overshoot(titleIn))).toFixed(1)}px)`,
            }}
          >
            {GENERATING.title}
          </h1>

          {/*
           * The decode is a visual effect, and the live region must never hear
           * it: `line.text` is re-scrambled every frame, so left readable this
           * announced some fifty half-noise strings on the way to four
           * sentences. So the animated copy is hidden and the finished tally
           * sits beside it in one block — the same treatment `RoundTransition`
           * gives its decoding step labels, for the same reason.
           *
           * Static from the first render, so the live region has one thing to
           * say and says it once. The tally cannot change while this is up:
           * the job is already closed, and the screen exists to report it.
           */}
          <div className={styles.lines} aria-hidden="true">
            {lines.map((line, index) => (
              <div
                key={index}
                className={styles.line}
                style={{ opacity: line.visible ? 1 : 0, color: line.color }}
              >
                {line.text}
              </div>
            ))}
          </div>
          <p className={styles.srOnly}>{GENERATING.spoken(tally)}</p>
        </div>
      </div>
    </div>
  )
}
